/**
 * Renders the per-template parameter form. Used by the search page (run a
 * template against ad-hoc values) and the schedules modal (lock in values
 * to fire on the cron). Stateless — owner controls the values map.
 *
 * Each `kind` controls the renderer:
 *   string   → text input
 *   cents    → currency input with $ ↔ cents conversion on blur
 *   int      → number input
 *   enum     → <select> with `options`
 *   strings  → comma-separated text → string[]
 */
import { useState, useEffect } from 'react';
import type { TemplateParameterSpec } from '@sourcito/schemas';
import { Input, Label } from '../design';

export type TemplateParameterValues = Record<string, string | number | string[] | null>;

export function TemplateParameterForm({
  parameters,
  values,
  onChange,
  idPrefix = 'tpl',
}: {
  parameters: TemplateParameterSpec[];
  values: TemplateParameterValues;
  onChange: (next: TemplateParameterValues) => void;
  idPrefix?: string;
}) {
  // Initialize defaults for any parameter that doesn't have a value yet.
  useEffect(() => {
    let mutated = false;
    const next = { ...values };
    for (const p of parameters) {
      if (next[p.path] === undefined && p.defaultValue !== undefined && p.defaultValue !== null) {
        next[p.path] = p.defaultValue as string | number | string[];
        mutated = true;
      }
    }
    if (mutated) onChange(next);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [parameters]);

  function update(path: string, value: string | number | string[] | null) {
    onChange({ ...values, [path]: value });
  }

  if (parameters.length === 0) {
    return (
      <p className="text-xs text-fg-tertiary italic">
        This template has no swappable parameters — it'll run as-is.
      </p>
    );
  }

  return (
    <div className="space-y-3">
      {parameters.map((p) => (
        <ParameterField
          key={p.path}
          parameter={p}
          value={values[p.path] ?? null}
          onChange={(v) => update(p.path, v)}
          fieldId={`${idPrefix}-${p.path.replace(/\./g, '-')}`}
        />
      ))}
    </div>
  );
}

function ParameterField({
  parameter,
  value,
  onChange,
  fieldId,
}: {
  parameter: TemplateParameterSpec;
  value: string | number | string[] | null;
  onChange: (next: string | number | string[] | null) => void;
  fieldId: string;
}) {
  const label = (
    <Label htmlFor={fieldId}>
      {parameter.label}
      {parameter.required && <span className="text-negative ml-0.5">*</span>}
    </Label>
  );

  if (parameter.kind === 'string') {
    return (
      <div className="space-y-1.5">
        {label}
        <Input
          id={fieldId}
          type="text"
          value={typeof value === 'string' ? value : ''}
          onChange={(e) => onChange(e.target.value || null)}
          maxLength={200}
        />
        {parameter.helpText && <p className="text-xs text-fg-tertiary">{parameter.helpText}</p>}
      </div>
    );
  }

  if (parameter.kind === 'int') {
    return (
      <div className="space-y-1.5">
        {label}
        <Input
          id={fieldId}
          type="number"
          value={typeof value === 'number' ? value : ''}
          onChange={(e) => {
            const v = e.target.value;
            onChange(v === '' ? null : Number(v));
          }}
        />
        {parameter.helpText && <p className="text-xs text-fg-tertiary">{parameter.helpText}</p>}
      </div>
    );
  }

  if (parameter.kind === 'cents') {
    return (
      <CentsField
        label={label}
        fieldId={fieldId}
        value={value}
        onChange={onChange}
        helpText={parameter.helpText}
      />
    );
  }

  if (parameter.kind === 'enum') {
    return (
      <div className="space-y-1.5">
        {label}
        <select
          id={fieldId}
          value={typeof value === 'string' ? value : ''}
          onChange={(e) => onChange(e.target.value || null)}
          className="w-full h-10 px-3 bg-surface border border-border-default rounded-sm text-base"
        >
          <option value="">— select —</option>
          {(parameter.options ?? []).map((opt) => (
            <option key={opt} value={opt}>
              {opt}
            </option>
          ))}
        </select>
        {parameter.helpText && <p className="text-xs text-fg-tertiary">{parameter.helpText}</p>}
      </div>
    );
  }

  // strings — comma-separated.
  const asArray = Array.isArray(value) ? value : [];
  return (
    <div className="space-y-1.5">
      {label}
      <Input
        id={fieldId}
        type="text"
        defaultValue={asArray.join(', ')}
        onBlur={(e) => {
          const parts = e.target.value
            .split(',')
            .map((s) => s.trim())
            .filter(Boolean);
          onChange(parts.length === 0 ? null : parts);
        }}
        placeholder="Comma-separated (e.g. AppleBrand, NestBrand)"
      />
      {parameter.helpText && <p className="text-xs text-fg-tertiary">{parameter.helpText}</p>}
    </div>
  );
}

function CentsField({
  label,
  fieldId,
  value,
  onChange,
  helpText,
}: {
  label: React.ReactNode;
  fieldId: string;
  value: string | number | string[] | null;
  onChange: (next: number | null) => void;
  helpText?: string;
}) {
  // Local string state for typing UX — only commit cents on blur. This lets
  // the user type "12.50" without each keystroke truncating.
  const initial = typeof value === 'number' ? (value / 100).toFixed(2) : '';
  const [text, setText] = useState(initial);

  useEffect(() => {
    if (typeof value === 'number') setText((value / 100).toFixed(2));
    else if (value === null) setText('');
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [value]);

  return (
    <div className="space-y-1.5">
      {label}
      <div className="relative">
        <span className="absolute left-3 top-1/2 -translate-y-1/2 text-fg-tertiary text-sm pointer-events-none">
          $
        </span>
        <Input
          id={fieldId}
          type="text"
          inputMode="decimal"
          value={text}
          onChange={(e) => setText(e.target.value)}
          onBlur={() => {
            const trimmed = text.trim();
            if (!trimmed) {
              onChange(null);
              return;
            }
            const dollars = Number(trimmed);
            if (!Number.isFinite(dollars) || dollars < 0) {
              onChange(null);
              setText('');
              return;
            }
            onChange(Math.round(dollars * 100));
          }}
          className="pl-7"
          placeholder="0.00"
        />
      </div>
      {helpText && <p className="text-xs text-fg-tertiary">{helpText}</p>}
    </div>
  );
}
